// 다이아몬드 아이템 클래스 - Item을 상속
class Diamond extends Item {
  constructor(scene, x = 0, z = 0, diamondType = "DIAMOND_GREEN") {
    super(scene, x, z);

    // 다이아몬드 타입에 따른 설정 적용
    const diamondConfig =
      ItemConfig.DIAMOND[diamondType] || ItemConfig.DIAMOND.DIAMOND_GREEN;
    this.modelPath = diamondConfig.MODEL_PATH;
    this.pointsValue = diamondConfig.POINTS_VALUE;
    this.diamondType = diamondType; // 다이아몬드 타입 저장

    // 생명주기
    this.createdTime = Date.now() / 1000;
    this.lifespan = 300.0;

    this.scale = 0.8;

    // 자석 효과 관련 속성
    this.magnetized = false;
    this.magnetSpeed = ItemConfig.MAGNET.MAGNET_SPEED; // 자석으로 끌려가는 속도
    this.isMagnetAnimating = false; // 자석 애니메이션 상태
    this.playerRef = null; // 플레이어 참조 저장
  }

  // 다이아몬드 업데이트 (부모 클래스와 기존 로직 결합)
  update(delta, playerPosition = null) {
    if (!this.model || this.isCollected) return;

    const currentTime = Date.now() / 1000;

    // 생명주기 체크
    if (currentTime - this.createdTime > this.lifespan) {
      this.destroy();
      return;
    }

    // 자석 효과가 활성화된 경우는 이미 playMagnetEffect에서 처리됨

    // 부모 클래스의 기본 업데이트 (회전, 호버링)
    super.update(delta);

    // 수명이 얼마 남지 않으면 깜빡임 효과
    const remainingTime = this.lifespan - (currentTime - this.createdTime);
    if (remainingTime < 5.0) {
      const blinkSpeed = 8.0; // 깜빡임 속도
      const opacity = 0.3 + 0.7 * Math.abs(Math.sin(currentTime * blinkSpeed));

      this.model.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.opacity = opacity;
        }
      });
    }
  }

  // 다이아몬드 수집 처리 (자석 효과와 일반 수집 구분)
  collect(player) {
    if (this.isCollected) return 0;

    // 자석 애니메이션이 진행 중이면 수집을 지연시킴 (애니메이션 완료까지 기다림)
    if (this.magnetized && this.isMagnetAnimating) {
      // Utils.log(
      //   `💎 다이아몬드가 자석 애니메이션 중이므로 수집을 지연시킵니다.`
      // );
      // 플레이어 참조를 저장하고 애니메이션 완료 시 수집되도록 함
      this.collectedPlayer = player;
      return 0; // 아직 수집되지 않았으므로 0 반환
    }

    this.isCollected = true;

    // 플레이어 정보를 저장해두고 애니메이션 끝에 효과 적용
    this.collectedPlayer = player;

    // 일반 수집: 부모 클래스의 멋진 수집 애니메이션 사용 (플레이어 참조 전달)
    this.playAdvancedCollectionEffect(player);

    return this.pointsValue;
  }

  // 다이아몬드 아이템 효과 적용
  applyEffect(player) {
    if (!player) return;

    // 현재 스테이지에 따른 배율 계산
    let stageScaling = 1.0;
    if (window.game && window.game.spawnScheduler) {
      const currentStage = window.game.spawnScheduler.currentStage;
      stageScaling = EnemySpawnConfig.getStageScaling(currentStage);
    }

    // 스테이지 배율이 적용된 포인트 증가
    const scaledPoints = Math.floor(this.pointsValue * stageScaling);
    player.addPoints(scaledPoints);
  }

  // 다이아몬드 타입별 색상 매핑
  getDiamondColor() {
    const colorMap = {
      DIAMOND_GREEN: { base: 0x004400, glow: 0x006600 }, // 초록색
      DIAMOND_BLUE: { base: 0x000044, glow: 0x0066ff }, // 파란색
      DIAMOND_RED: { base: 0x440000, glow: 0x660000 }, // 빨간색
      DIAMOND_YELLOW: { base: 0x444400, glow: 0x666600 }, // 노란색
    };
    return colorMap[this.diamondType] || colorMap.DIAMOND_GREEN;
  }

  // 다이아몬드만의 발광 효과 (타입별 색상)
  applyCollectionGlow(child, progress) {
    // 다이아몬드 타입에 따른 색상 적용
    const colors = this.getDiamondColor();
    const glowIntensity = colors.base + Math.floor(colors.glow * progress);
    child.material.emissive.setHex(glowIntensity);
  }

  // 다이아몬드 수집 효과 연출
  playCollectionEffect() {
    // 다이아몬드 특유의 반짝임 효과
    // Utils.log("💎 다이아몬드 수집 효과!");

    if (this.collectedPlayer) {
      this.applyEffect(this.collectedPlayer);
    }
  }

  // 다이아몬드 제거 (부모 클래스 방식 활용)
  destroy() {
    if (this.model) {
      this.scene.remove(this.model);
      this.model = null;
    }
    this.isCollected = true;
    // Utils.log("다이아몬드가 사라졌습니다");
  }

  // 플레이어와의 충돌 검사 (부모 클래스 방식 오버라이드)
  checkCollision(playerPosition) {
    if (!this.isLoaded || this.isCollected) return false;

    const distance = Utils.distance(this.position, playerPosition);
    return distance <= this.collectionRadius;
  }

  // 다이아몬드 위치 반환
  getPosition() {
    return this.model ? this.model.position : this.position;
  }

  // 자석 효과 활성화 - playAdvancedCollectionEffect 재사용
  activateMagnet(playerPosition, playerRef = null) {
    if (this.magnetized || this.isCollected) return;

    this.magnetized = true;
    this.playerRef = playerRef; // 플레이어 참조 저장
    // Utils.log("💎 다이아몬드에 자석 효과 활성화!");

    // 수집 애니메이션을 플레이어 참조로 시작 (자석 효과)
    if (playerRef) {
      this.playMagnetEffect(playerRef);
    }
  }

  // 자석 효과 비활성화
  deactivateMagnet() {
    this.magnetized = false;
  }

  // 자석 효과로 플레이어에게 빨려들어가는 애니메이션 - 바로 빨려들어오기
  playMagnetEffect(player) {
    if (!this.model || this.isCollected) return;

    // 이미 빨려들어가고 있으면 중복 실행 방지
    if (this.isMagnetAnimating) return;
    this.isMagnetAnimating = true;

    const startPosition = this.model.position.clone();
    const startScale = this.model.scale.x;

    // 자석 효과 애니메이션 지속 시간 (바로 빨려들어가므로 더 짧게)
    const totalDuration = 1.0; // 1.0초

    const startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const progress = Math.min(elapsed / totalDuration, 1.0);

      if (progress < 1.0 && !this.isCollected) {
        // 실시간으로 플레이어 위치 가져오기
        const currentPlayerPosition = player.position.clone();

        // 자석 효과: 바로 플레이어 위치로 빨려들어가기
        const easeIn = Math.pow(progress, 3); // 더 강한 easeIn 곡선으로 급가속 효과

        // 플레이어 위치로 빨려들어가기 (곡선 경로로, 실시간 위치 사용)
        const currentPos = startPosition
          .clone()
          .lerp(currentPlayerPosition, easeIn);

        // 자연스러운 곡선 궤적을 위한 사인파 추가
        const waveOffset =
          Math.sin(progress * Math.PI * 2) * 0.5 * (1 - progress);
        const perpendicular = new THREE.Vector3()
          .subVectors(currentPlayerPosition, startPosition)
          .cross(new THREE.Vector3(0, 1, 0))
          .normalize()
          .multiplyScalar(waveOffset);

        currentPos.add(perpendicular);
        this.model.position.copy(currentPos);

        // 크기 줄어들면서 회전 가속 (더 드라마틱하게)
        const scaleMultiplier = 1.3 - 1.2 * easeIn;
        this.model.scale.setScalar(
          startScale * Math.max(0.05, scaleMultiplier)
        );

        // 회전 속도 급격히 증가 (빨려들어가는 느낌)
        this.model.rotation.y +=
          this.rotationSpeed * 8 * progress * progress * elapsed;
        this.model.rotation.x += this.rotationSpeed * 3 * progress * elapsed;

        // 투명도 감소 (마지막 0.3초 동안 더 길게)
        const fadeProgress = Math.max(0, (progress - 0.5) / 0.5);
        const opacity = 1.0 - fadeProgress * fadeProgress; // 부드러운 페이드
        this.model.traverse((child) => {
          if (child.isMesh && child.material) {
            child.material.opacity = opacity;

            // 발광 효과 강화 (다이아몬드 타입별 색상)
            this.applyMagnetGlow(child, progress);
          }
        });

        requestAnimationFrame(animate);
      } else {
        // 자석 효과 완료
        this.isMagnetAnimating = false;

        // 플레이어에게 도달하면 자동 수집 (애니메이션 완료 후 확실히 수집)
        if (!this.isCollected) {
          this.isCollected = true;

          // 저장된 플레이어 참조 사용 (collect에서 저장된 참조 우선, 없으면 playerRef 사용)
          const targetPlayer = this.collectedPlayer || this.playerRef;
          if (targetPlayer) {
            this.collectedPlayer = targetPlayer;
            this.playCollectionEffect(); // 포인트 증가 등 효과만 적용
            // Utils.log(`💎 다이아몬드가 자석 효과로 수집되었습니다!`);
          } else {
            // Utils.log(
            //   "자석 효과로 수집되었지만 플레이어 참조가 없습니다.",
            //   "warn"
            // );
          }
        }

        this.destroy();
      }
    };

    animate();
  }

  // 자석 발광 효과 (다이아몬드 타입별 색상 + 자석 효과)
  applyMagnetGlow(child, progress) {
    // 다이아몬드 타입에 따른 색상 + 자석 효과를 위한 추가 발광
    const colors = this.getDiamondColor();
    const magnetGlow = 0x002222; // 자석 효과를 위한 추가 발광
    const glowIntensity =
      colors.base +
      Math.floor(colors.glow * progress) +
      Math.floor(magnetGlow * progress);
    child.material.emissive.setHex(glowIntensity);
  }

  // 다이아몬드 정보 반환
  getInfo() {
    const currentTime = Date.now() / 1000;
    const remainingTime = Math.max(
      0,
      this.lifespan - (currentTime - this.createdTime)
    );

    return {
      position: this.getPosition(),
      pointsValue: this.pointsValue,
      isCollected: this.isCollected,
      remainingTime: remainingTime,
      magnetized: this.magnetized,
    };
  }
}

// webpack 빌드를 위해 default export 추가
export default Diamond;

// 전역 변수로도 등록 (기존 코드 호환성)
window.Diamond = Diamond;
